import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { companyBillingSitesPath, EDIT_FIELDS, Size, lineDiscountOf, t, type DividerRow, documentName, newDocumentTitle } from '@bms2/shared';
import { DatePicker, Dropdown, NumericInput, Select, TextInput } from '../../components/controls';
import { ItemTable, storedId, hasUnnamedDivider, newLine, savedRows, withDividers, type ItemLineDraft } from '../../components/composites/ItemTable';
import { ModalField } from '../../components/modals/ModalShell';
import { CustomerNotesBox } from './CustomerNotesBox';
import { DocumentEditPage, documentEditStyles as styles } from './DocumentEditPage';
import { ShipmentPreferencePicker } from './ShipmentPreferencePicker';
import { SitePicker, useSiteChoices } from './SitePicker';
import { SALES_ORDERS_PATH, post, postingAccountSearch, put, taxCategorySearch, useApi } from './api';
import { useCompanySearch, useItemPicker } from '../settings/api';
import { previewLinesOf, useTaxPreview } from './useTaxPreview';
import { useAddNew } from './AddNewHost';
import { useDocumentToday } from '../../shell/ViewerZone';
import { discountFieldsOf, discountLevelOf } from './lineDiscountFields';

/** 🔨 A row the grid added carries no stored id; the save posts it as a new line rather than as an edit of one (decision 0445). */

/** What the form opens on: the account, its open sites with the two the rules pick, and a stored Draft's own header. */
export interface SalesOrderDefaults {
  companyId: string;
  companyName: string;
  paymentTerms: string;
  sites: { id: string; label: string }[];
  billToSiteId: string | null;
  shipToSiteId: string | null;
  deal: { id: string; name: string } | null;
  displayMode: string;
  customerPoNumber: string | null;
  /** 🔒 The customer PO's amount in cents (decision 2594); absent from an older reply. */
  customerPoAmountCents?: number | null;
  /** 🔒 The day the order is dated; a new order opens on today in the viewer's own zone instead (decisions 0681, 1030). */
  documentDate?: string | null;
  /** 🔒 How the goods travel (decision 0682); absent from an older reply. */
  shipmentPreferenceId?: string | null;
  /** 🔒 The notes to the customer Region 7 prints (decision 0683); absent from an older reply. */
  customerNotes?: string | null;
  salesOrderId?: string | null;
  salesOrderNumber?: string | null;
  /** The Quote a stored order was converted from; its two sites are then the Quote's and do not change here. */
  sourceQuoteId?: string | null;
  /** 🔒 The Discount Scope setting, so the Discount column follows it rather than a literal (decisions 0097, 1031); absent from an older reply. */
  discountLevel?: string;
}

const displayModes = EDIT_FIELDS.Quote.find((field) => field.key === 'displayMode')?.options ?? [];

/**
 * 🔒 The Sales Order's edit mode (decision 0409), on the order's own page and
 * never in a modal. The header fields run account, bill-to, ship-to, the
 * order's own date, the customer's own purchase order number, the display mode
 * and the number; the lines follow on the Item Table's sales column set; and
 * the sticky footer carries Save as Draft, Save and Send and Cancel.
 *
 * 🔒 The date is the order's own (T114; decision 0681): the tax preview and the
 * save both read it, so a backdated order is taxed at the rate of its day.
 * 🔨 The terms are still the account's and are shown, never typed.
 */
export function SalesOrderForm({ salesOrderId = null, apiBaseUrl = '' }: { salesOrderId?: string | null; apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const dealFromUrl = params.get('dealId');
  const companyFromUrl = params.get('companyId');
  /** The account picked here where the entry point supplied none; the list's + New is the one that does. */
  const [account, setAccount] = useState<{ id: string; label: string } | null>(null);
  const companyId = companyFromUrl || account?.id || '';
  const query = salesOrderId
    ? `salesOrderId=${encodeURIComponent(salesOrderId)}`
    : dealFromUrl
    ? `dealId=${encodeURIComponent(dealFromUrl)}`
    : companyId
    ? `companyId=${encodeURIComponent(companyId)}`
    : null;
  // 🔨 No account, nothing to read: the form asks for one rather than reading defaults for the empty string and failing.
  const defaults = useApi<SalesOrderDefaults>(query ? `${SALES_ORDERS_PATH}/defaults?${query}` : null, apiBaseUrl);
  const answer = defaults.state.kind === 'ready' ? defaults.state.data : null;
  // 🔒 The account's sites, read again once + Add New Site writes one (T185).
  const siteChoices = useSiteChoices(answer, answer ? companyBillingSitesPath(answer.companyId) : null, apiBaseUrl);
  const siteAdded = siteChoices.added;
  // 🔒 A converted order's sites are the Quote's (piece 22), so neither picker nor its + Add New Site is offered, and the form says why
  // rather than writing a site the save then refuses (found in review, 2026-09-22).
  const sitesLocked = answer?.sourceQuoteId ? t('salesOrder.edit.sitesFromQuote') : null;
  const needsAccount = query === null;
  const asksForAccount = !salesOrderId && !dealFromUrl && !companyFromUrl;
  const readFailed = defaults.state.kind === 'failed';
  const storedOrder = useApi<{ lines: { id: string; itemId: string | null; itemName?: string | null; sku: string | null; description: string; quantity: number; unitName: string; unitPriceCents: number | null; taxCategoryId: string; taxCategoryName: string; taxAmountCents: number; accountId: string; accountLabel: string; budgetedHours?: number | null; isService?: boolean; discountType?: string | null; discountValue?: number | null }[]; dividers?: DividerRow[] }>(
    salesOrderId ? `${SALES_ORDERS_PATH}/${salesOrderId}` : null,
    apiBaseUrl,
  );

  const [billTo, setBillTo] = useState<string | null>(null);
  const [shipTo, setShipTo] = useState<string | null>(null);
  const [customerPo, setCustomerPo] = useState('');
  const [poAmount, setPoAmount] = useState<number | null>(null);
  // 🔒 Opens on today in the viewer's own zone, through the hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [documentDate, setDocumentDate] = useState(today);
  const [shipmentPreference, setShipmentPreference] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [displayMode, setDisplayMode] = useState('Parts');
  // 🔒 A fresh document opens on one empty line, so the table is typed into rather than found (decision 0447).
  const [drafts, setDrafts] = useState<ItemLineDraft[]>(() => [newLine()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // 🔒 A picked Item brings its SKU and its sales description, as the purchase side's lines always did (T121; piece 4, the SKU under the name; decision 0303).
  // 🔒 Which picked Items are Services, which draws the Budget (hours) cell (decision 0860).

  // The defaults arriving after the form opened fill the fields nobody has touched.
  const [seen, setSeen] = useState<SalesOrderDefaults | null>(null);
  if (answer && seen !== answer) {
    setSeen(answer);
    setBillTo(answer.billToSiteId);
    setShipTo(answer.shipToSiteId);
    setCustomerPo(answer.customerPoNumber ?? '');
    setPoAmount(answer.customerPoAmountCents ?? null);
    setDocumentDate(salesOrderId ? (answer.documentDate ?? today) : today);
    setShipmentPreference(answer.shipmentPreferenceId ?? '');
    setCustomerNotes(answer.customerNotes ?? '');
    if (answer.displayMode) setDisplayMode(answer.displayMode);
  }

  const rows = storedOrder.state.kind === 'ready' ? storedOrder.state.data.lines : null;
  const storedDividers = storedOrder.state.kind === 'ready' ? storedOrder.state.data.dividers : null;
  const [seenLines, setSeenLines] = useState<unknown>(null);
  if (rows && seenLines !== rows) {
    setSeenLines(rows);
    // 🔨 The stored category reopens with the line; a rewrite without it re-defaults the line to the Item's (decision 0435).
    // 🔒 A stored line reopens on its Item's own name, never on its description (T118; decision 0640).
    // 🔒 The stored group dividers stand among the lines where they were saved (decision 0650).
    // 🔒 A stored line reopens on its own discount, which the save sends back whatever the setting (decision 1031).
    setDrafts(withDividers(rows.map((row) => ({ id: row.id, itemId: row.itemId, itemLabel: row.itemName ?? row.description, sku: row.sku, description: row.description, qty: row.quantity, unit: row.unitName, rateCents: row.unitPriceCents, accountId: row.accountId, accountLabel: row.accountLabel, taxCategoryId: row.taxCategoryId, taxLabel: row.taxCategoryName, freeText: row.itemId === null, service: row.isService === true, budgetedHours: row.budgetedHours ?? null, discount: lineDiscountOf(row.discountType, row.discountValue) })), storedDividers));
  }
  const accountSearch = useCompanySearch(apiBaseUrl, 'customer', { activeOnly: true });
  const { unitIds, pickerProps } = useItemPicker(apiBaseUrl, 'sales');

  const taxSearch = useMemo(() => taxCategorySearch(apiBaseUrl), [apiBaseUrl]);
  // 🔒 The GL account a line posts to; the header's own picker is the customer Account and the two are never one control (decision 0435).
  const lineAccountSearch = useMemo(() => postingAccountSearch(apiBaseUrl), [apiBaseUrl]);

  // 🔒 The lines the save keeps, and the group dividers at their places among them (decision 0650).
  const saveRows = () =>
    savedRows(
      drafts,
      (d) => Boolean(d.itemId || d.itemLabel.trim() || d.description.trim()) && (d.qty ?? 0) > 0,
      (d) => ({
        id: storedId(d),
        itemId: d.itemId,
        description: (d.freeText ? d.itemLabel : d.description).trim() || null,
        quantity: d.qty ?? 1,
        // The unit the grid's closed list wrote wins over the Item's own, which is what an override means (decision 0439).
        unitId: d.unitId ?? (d.itemId ? unitIds.current.get(d.itemId) ?? null : null),
        unitPriceCents: d.rateCents,
        // 🔨 The category and the account travel; the amount never does, because the save re-derives it over the resolved lines (decision 0435).
        taxCategoryId: d.taxCategoryId ?? rows?.find((row) => row.id === d.id)?.taxCategoryId ?? null,
        accountId: d.accountId ?? rows?.find((row) => row.id === d.id)?.accountId ?? null,
        // 🔒 A service line's budget in hours; any other line sends none (decision 0860).
        budgetedHours: d.service ? d.budgetedHours ?? null : null,
        // 🔒 The discount the line carries goes back with it, typed under Line or held since the Quote (decision 1031).
        ...discountFieldsOf(d.discount),
      }),
    );

  // 🔒 The tax is worked out live, through the call the save makes, on every change to a line or the Ship-To (decision 0644).
  const preview = useTaxPreview(
    answer ? { documentType: 'SalesOrder', documentId: salesOrderId, shipToSiteId: shipTo || null, documentDate: documentDate || null, lines: previewLinesOf(drafts, (d) => ({ taxCategoryId: d.taxCategoryId ?? rows?.find((row) => row.id === d.id)?.taxCategoryId ?? null, ...discountFieldsOf(d.discount) })) } : null,
    apiBaseUrl,
  );

  const save = async (then: 'view' | 'send'): Promise<void> => {
    if (needsAccount) return setError(t('salesOrder.new.chooseAccount'));
    if (!answer) return setError(t('salesOrder.new.readFailed'));
    if (!billTo) return setError(t('salesOrder.new.needsBillTo'));
    if (hasUnnamedDivider(drafts)) return setError(t('item.dividerNeedsName'));
    const { lines, dividers } = saveRows();
    if (lines.length === 0) return setError(t('salesOrder.new.needsLines'));
    setBusy(true);
    const body = { companyId: answer.companyId, dealId: answer.deal?.id ?? null, billToSiteId: billTo, shipToSiteId: shipTo || null, displayMode, customerPoNumber: customerPo.trim() || null, customerPoAmountCents: poAmount, documentDate: documentDate || null, shipmentPreferenceId: shipmentPreference || null, customerNotes, lines, dividers };
    const saved = salesOrderId
      ? await put<{ id: string }>(`${SALES_ORDERS_PATH}/${salesOrderId}`, body, apiBaseUrl)
      : await post<{ id: string }>(SALES_ORDERS_PATH, body, apiBaseUrl);
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    // 🔨 Save and Send saves first and opens the send on the saved order, so a send that fails leaves a saved order.
    navigate(`/sales-orders/${salesOrderId ?? saved.data.id}${then === 'send' ? '?act=send' : ''}`);
  };

  return (
    <>
    <DocumentEditPage
      title={answer?.salesOrderNumber ?? newDocumentTitle('SalesOrder')}
      subtitle={answer?.companyName ?? account?.label ?? documentName('SalesOrder')}
      outwardLabel={t('document.edit.saveAndSend')}
      busy={busy}
      error={error ?? (readFailed ? t('salesOrder.new.readFailed') : null)}
      onDismissError={() => setError(null)}
      onSaveDraft={() => void save('view')}
      onSaveAndSend={() => void save('send')}
      onCancel={() => navigate(salesOrderId ? `/sales-orders/${salesOrderId}` : '/sales-orders')}
      header={
        <>
          {asksForAccount ? (
            <div data-sales-order-field="account" data-sales-order-account={answer?.companyId ?? ''}>
              <Dropdown size={Size.Medium} label={t('salesOrder.new.account')} entityLabel={t('salesOrder.new.accountEntity')} search={accountSearch} value={account?.label ?? null} onSelect={(row) => setAccount({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'customer' }, (row) => setAccount({ id: row.id, label: row.line1 }))} required />
            </div>
          ) : (
            <ModalField label={t('salesOrder.new.account')}>
              <span data-sales-order-account={answer?.companyId ?? ''}>{answer?.companyName ?? account?.label ?? ''}</span>
            </ModalField>
          )}
          {answer?.deal ? (
            <ModalField label={t('salesOrder.fact.deal')}>
              <span data-sales-order-deal={answer.deal.id}>{answer.deal.name}</span>
            </ModalField>
          ) : null}
          <div className={styles.sitePicker}>
            <SitePicker picker="billTo" label={t('salesOrder.new.billTo')} value={billTo} onChange={setBillTo} sites={siteChoices.sites} companyId={answer?.companyId ?? null} companyName={answer?.companyName ?? null} apiBaseUrl={apiBaseUrl} lockedReason={sitesLocked} required onCreated={siteAdded(setBillTo)} data={{ 'data-sales-order-site': 'billTo' }} />
            {answer && siteChoices.sites.length === 0 ? <p className={styles.hint} data-sales-order-no-sites="true">{t('salesOrder.new.noSites')}</p> : null}
          </div>
          <div className={styles.sitePicker}>
            <SitePicker picker="shipTo" label={t('salesOrder.new.shipTo')} value={shipTo} onChange={setShipTo} sites={siteChoices.sites} companyId={answer?.companyId ?? null} companyName={answer?.companyName ?? null} apiBaseUrl={apiBaseUrl} lockedReason={sitesLocked} placeholder={t('salesOrder.new.notChosen')} onCreated={siteAdded(setShipTo)} data={{ 'data-sales-order-site': 'shipTo' }} />
          </div>
          {sitesLocked ? <p className={styles.hint} data-sales-order-sites-locked="true">{sitesLocked}</p> : null}
          <div data-sales-order-field="date">
            <DatePicker size={Size.Small} label={t('document.edit.date')} value={documentDate} onChange={setDocumentDate} mode="date" name="salesOrderDate" />
          </div>
          <ShipmentPreferencePicker value={shipmentPreference} onChange={setShipmentPreference} apiBaseUrl={apiBaseUrl} name="salesOrder" />
          <TextInput size={Size.Medium} label={t('salesOrder.new.customerPo')} value={customerPo} onChange={setCustomerPo} name="salesOrderCustomerPo" />
          {/* 🔒 Decision 2594 (T1175): the amount the customer's purchase order covers, typed beside its number. */}
          <NumericInput size={Size.Medium} kind="currency" label={t('jobBilling.po.amount')} value={poAmount} onChange={setPoAmount} name="salesOrderCustomerPoAmount" hint={t('jobBilling.po.amountHint')} />
          <Select size={Size.Medium} label={t('salesOrder.new.displayMode')} value={displayMode} onChange={setDisplayMode} options={displayModes.map((o) => ({ value: o.value, label: o.label }))} dataAttribute={{ 'data-sales-order-display-mode': 'true' }} required />
          <ModalField label={t('salesOrder.new.paymentTerms')}>
            <span data-sales-order-terms="true">{answer?.paymentTerms ?? ''}</span>
          </ModalField>
          <ModalField label={t('salesOrder.form.number')}>
            <span data-sales-order-field="number">{answer?.salesOrderNumber ?? t('salesOrder.form.numberOnSave')}</span>
          </ModalField>
          <p className={styles.hint}>{t('salesOrder.new.customerPoHint')}</p>
          <CustomerNotesBox value={customerNotes} onChange={setCustomerNotes} name="salesOrder" />
        </>
      }
      lines={
        <ItemTable
          documentType="SalesOrder"
          // 🔒 Budget (hours) stands beside the quantity, drawn on a service line alone (decision 0860).
          columns={['item', 'qty', 'budget', 'unit', 'rate', 'account', 'tax', 'amount']}
          lines={drafts}
          onChange={setDrafts}
          // 🔒 The Discount column follows the Discount Scope setting the opening read names, as the Quote's does (decisions 0651, 1031).
          discountLevel={discountLevelOf(answer?.discountLevel)}
          {...pickerProps({ saleGuards: true, service: true })}
          accountSearch={lineAccountSearch}
          taxSearch={taxSearch}
          // 🔨 The figure under the category is the calculation's own, worked out live and never typed (decisions 0435, 0644).
          taxAmountCentsOf={(draft) => preview.taxCentsOf(draft.id) ?? rows?.find((row) => row.id === draft.id)?.taxAmountCents ?? null}
          totals={preview}
        />
      }
    />
    {addNew.host}
    </>
  );
}
